"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchProfileReviewsPage } from "@/lib/reviews/api-client";
import type { ProfileReviewRole, ProfileReviewStats, Review } from "@/lib/reviews/schema";

const PAGE_SIZE = 20;

export type ProfileReviewsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; reviews: Review[]; stats: ProfileReviewStats | null; hasMore: boolean; totalCount: number }
  | { kind: "error"; error: string };

export function useProfileReviews(
  profileId: string | null | undefined,
  role: ProfileReviewRole,
  enabled = true,
) {
  const [state, setState] = useState<ProfileReviewsState>({ kind: "idle" });
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);
  const requestIdRef = useRef(0);

  const loadPage = useCallback(
    async (opts: { reset: boolean; signal?: AbortSignal; requestId: number }) => {
      const id = profileId?.trim();
      if (!id || !enabled) {
        setState({ kind: "idle" });
        return;
      }
      const offset = opts.reset ? 0 : offsetRef.current;
      if (opts.reset) {
        setState({ kind: "loading" });
      } else {
        setLoadingMore(true);
      }
      try {
        const page = await fetchProfileReviewsPage({
          profileId: id,
          role,
          offset,
          limit: PAGE_SIZE,
          signal: opts.signal,
        });
        if (opts.requestId !== requestIdRef.current) return;
        offsetRef.current = offset + page.reviews.length;
        setState((prev) => {
          const prevReviews =
            !opts.reset && prev.kind === "success" ? prev.reviews : [];
          return {
            kind: "success",
            reviews: [...prevReviews, ...page.reviews],
            stats: page.stats ?? (prev.kind === "success" ? prev.stats : null),
            hasMore: page.has_more,
            totalCount:
              typeof page.total_count === "number"
                ? page.total_count
                : prev.kind === "success"
                  ? prev.totalCount
                  : 0,
          };
        });
      } catch (e) {
        if (opts.signal?.aborted) return;
        if (opts.requestId !== requestIdRef.current) return;
        setState({
          kind: "error",
          error: e instanceof Error ? e.message : "load_failed",
        });
      } finally {
        if (opts.requestId === requestIdRef.current) {
          setLoadingMore(false);
        }
      }
    },
    [enabled, profileId, role],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    offsetRef.current = 0;
    setLoadingMore(false);
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setState({ kind: "loading" });
    void loadPage({ reset: true, signal: ctrl.signal, requestId });
    return () => ctrl.abort();
  }, [loadPage]);

  const fetchNextPage = useCallback(() => {
    if (state.kind !== "success" || !state.hasMore || loadingMore) return;
    void loadPage({ reset: false, requestId: requestIdRef.current });
  }, [loadPage, loadingMore, state]);

  const reload = useCallback(() => {
    offsetRef.current = 0;
    requestIdRef.current += 1;
    void loadPage({ reset: true, requestId: requestIdRef.current });
  }, [loadPage]);

  return { state, loadingMore, fetchNextPage, reload };
}
